import React, { useState, useEffect, useContext, useRef } from 'react';
import Layout from './Layout';
import EventContext from '../contexts/EventContext';
import useApiUrlTranslation from './useApiUrlTranslation';
import Loader from './Loader';
import { Video, User, Building, Film, CheckCircle, XCircle, Mail, Copy, Sparkles, UploadCloud, Download, Loader as SpinnerIcon, Plus, X, Search, PenSquare, Settings, Trash2, AlertTriangle, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import DragDropVideoUpload from './DragDropVideoUpload';
import { API_URL } from '../config';
import RegistrationDashboardLayout from './RegistrationDashboardLayout';
import { logActivity, useActivityLog } from '../customHooks/useActivityLog';

const UPLOAD_API_URL = 'https://manager.event2one.com/videos/upload-to-youtube/upload_api.php';
const UPLOAD_API_KEY = 'mgv_yt_upload_2026';

const AddSlotModal = ({ onClose, onCreated, currentEvent, apiUrlTranslation }) => {
    const [mode, setMode] = useState('search'); // 'search' | 'create'
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ prenom: '', nom: '', societe: '', mail: '', punchline: '', sn_linkedin: '' });

    const searchTimeout = useRef(null);

    const handleQueryChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        setSelectedContact(null);
        clearTimeout(searchTimeout.current);
        if (val.length < 2) { setResults([]); return; }
        searchTimeout.current = setTimeout(async () => {
            setSearching(true);
            try {
                const words = val.trim().split(/\s+/).filter(Boolean);
                const conditions = words.map(w =>
                    `(nom LIKE '%${w}%' OR prenom LIKE '%${w}%' OR societe LIKE '%${w}%' OR CONCAT(prenom,' ',nom) LIKE '%${w}%' OR CONCAT(nom,' ',prenom) LIKE '%${w}%')`
                ).join(' AND ');
                const sql = `WHERE ${conditions} ORDER BY certified desc, nom LIMIT 10`;
                const res = await fetch(`${apiUrlTranslation}?action=getContactList&params=${encodeURIComponent(sql)}`);
                const data = await res.json();
                setResults(Array.isArray(data) ? data : []);
            } finally {
                setSearching(false);
            }
        }, 400);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            let id_contact = selectedContact?.id_contact;

            // 1. Créer le contact si nouveau
            if (!id_contact) {
                if (!form.prenom || !form.nom) { alert('Prénom et nom requis.'); return; }
                const res = await fetch(`${API_URL}?action=createContact`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(form),
                });
                const rawContact = await res.text();
                console.log('[createContact] raw response:', rawContact);
                const dataContact = JSON.parse(rawContact);
                id_contact = typeof dataContact === 'string' || typeof dataContact === 'number'
                    ? parseInt(dataContact, 10)
                    : dataContact?.id_contact;
                if (!id_contact) throw new Error(`Création du contact échouée (réponse: ${rawContact})`);
            }

            // 2. Créer le créneau conf_event
            const ceRes = await fetch(`${API_URL}?action=createConfEvent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_event: currentEvent.id_event }),
            });



            const ceRaw = await ceRes.text();
            const id_conf_event = parseInt(JSON.parse(ceRaw), 10);
            if (!id_conf_event) throw new Error(`Création du créneau échouée (réponse: ${ceRaw})`);

            // 3. Positionner le contact sur le créneau (statut 143)
            const confRes = await fetch(`${API_URL}?action=createConferencier`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_contact, id_conf_event, statut: 143, id_event: currentEvent.id_event }),
            });
            const confRaw = await confRes.text();
            console.log('[createConferencier] raw response:', confRaw);
            const confData = JSON.parse(confRaw);
            const id_conferencier = typeof confData === 'string' || typeof confData === 'number' ? parseInt(confData, 10) : confData?.id_conferencier;
            if (!id_conferencier) throw new Error(`Association contact/créneau échouée (réponse: ${confRaw})`);

            const contactObj = selectedContact || { id_contact, ...form };
            onCreated({ contact: contactObj, id_conf_event, id_conferencier });
        } catch (err) {
            alert(`Erreur : ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-neutral-900 rounded-fib-8 shadow-2xl w-full max-w-lg">
                <div className="flex items-center justify-between p-fib-5 border-b dark:border-neutral-800">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">Ajouter un slot de captation</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"><X size={20} /></button>
                </div>

                <div className="p-fib-5 space-y-fib-3">
                    {/* Toggle */}
                    <div className="flex rounded-lg border dark:border-neutral-700 overflow-hidden text-sm font-medium">
                        <button onClick={() => setMode('search')} className={`flex-1 py-2 ${mode === 'search' ? 'bg-e2o-500 text-white' : 'text-gray-600 hover:bg-gray-50 dark:text-neutral-300 dark:hover:bg-neutral-800'}`}>
                            Rechercher un contact existant
                        </button>
                        <button onClick={() => setMode('create')} className={`flex-1 py-2 ${mode === 'create' ? 'bg-e2o-500 text-white' : 'text-gray-600 hover:bg-gray-50 dark:text-neutral-300 dark:hover:bg-neutral-800'}`}>
                            Créer un nouveau contact
                        </button>
                    </div>

                    {mode === 'search' && (
                        <div className="space-y-3">
                            <div className="relative">
                                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500" />
                                <input
                                    type="text"
                                    placeholder="Nom, prénom ou société…"
                                    value={query}
                                    onChange={handleQueryChange}
                                    className="w-full pl-9 pr-3 py-2 border dark:border-neutral-700 rounded-lg text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-e2o-300"
                                />
                                {searching && <SpinnerIcon size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-e2o-400 animate-spin" />}
                            </div>
                            {results.length > 0 && !selectedContact && (
                                <ul className="border dark:border-neutral-700 rounded-lg divide-y dark:divide-neutral-700 max-h-48 overflow-y-auto bg-white dark:bg-neutral-800">
                                    {results.map(c => (
                                        <li key={c.id_contact}
                                            onClick={() => { setSelectedContact(c); setQuery(`${c.prenom} ${c.nom} — ${c.societe}`); setResults([]); }}
                                            className="flex items-center gap-3 px-3 py-2 hover:bg-e2o-50 dark:hover:bg-neutral-700 cursor-pointer text-sm text-gray-800 dark:text-white">
                                            {c.photos?.small && <img src={c.photos.small} alt={`${c.prenom} ${c.nom}`} className="w-8 h-8 rounded-full object-cover" />}
                                            <span className="font-medium">{c.prenom} {c.nom}</span>
                                            <span className="text-gray-400 dark:text-neutral-500 truncate">{c.societe}</span>
                                            {c.certified == 1 && <span className="ml-auto text-xs text-green-600 font-semibold">Certifié</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {selectedContact && (
                                <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2 text-sm text-green-800 dark:text-green-300">
                                    <CheckCircle size={14} />
                                    <span>{selectedContact.prenom} {selectedContact.nom} — {selectedContact.societe}</span>
                                    <button onClick={() => { setSelectedContact(null); setQuery(''); }} className="ml-auto text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"><X size={13} /></button>
                                </div>
                            )}
                        </div>
                    )}

                    {mode === 'create' && (
                        <div className="grid grid-cols-2 gap-3">
                            {[['prenom', 'Prénom *'], ['nom', 'Nom *'], ['societe', 'Société'], ['mail', 'Email'], ['punchline', 'Punchline'], ['sn_linkedin', 'Profil LinkedIn']].map(([key, label]) => (
                                <div key={key} className={key === 'punchline' ? 'col-span-2' : ''}>
                                    <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">{label}</label>
                                    <input
                                        type={key === 'mail' ? 'email' : 'text'}
                                        value={form[key]}
                                        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                                        className="w-full p-2 border dark:border-neutral-700 rounded-lg text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-e2o-300"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-fib-3 px-fib-5 pb-fib-5">
                    <button onClick={onClose} className="px-fib-3 py-fib-2 text-sm text-gray-600 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-fib-5">Annuler</button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || (mode === 'search' && !selectedContact)}
                        className="flex items-center gap-fib-2 px-fib-3 py-fib-2 text-sm font-medium text-white bg-e2o-500 hover:bg-e2o-600 rounded-fib-5 disabled:opacity-50"
                    >
                        {submitting ? <SpinnerIcon size={13} className="animate-spin" /> : <Plus size={13} />}
                        {submitting ? 'Création…' : 'Créer le slot'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const VideoEdit = ({ video, aiSuggestions, chapters: chaptersProp, idContact, idPresta, participants = [] }) => {
    const [titre, setTitre] = useState(video?.titre || '');
    const [description, setDescription] = useState(video?.description || '');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [localAi, setLocalAi] = useState(aiSuggestions || null);
    const [aiLoading, setAiLoading] = useState(false);
    const [localChapters, setLocalChapters] = useState(chaptersProp || null);
    const [chaptersLoading, setChaptersLoading] = useState(false);
    const [transcribeStatus, setTranscribeStatus] = useState('idle'); // idle | uploading | done | error
    const [transcribeProgress, setTranscribeProgress] = useState(0);
    const transcribeInputRef = useRef(null);
    const prevVideoIdRef = useRef(video?.id_video || null);

    useEffect(() => {
        if (aiSuggestions) {
            setLocalAi(aiSuggestions);
            if (aiSuggestions.titre) setTitre(aiSuggestions.titre);
            if (aiSuggestions.description) setDescription(aiSuggestions.description);
        }
    }, [aiSuggestions]);

    useEffect(() => {
        if (chaptersProp?.length) setLocalChapters(chaptersProp);
    }, [chaptersProp]);

    // Auto-suggestion quand une vidéo vient d'être uploadée (id_video passe de null à une valeur)
    // Skippé si l'upload a déjà retourné des suggestions IA (aiSuggestions prop)
    useEffect(() => {
        const newId = video?.id_video;
        const prevId = prevVideoIdRef.current;
        prevVideoIdRef.current = newId || null;
        if (newId && !prevId && !aiSuggestions) {
            handleAiSuggest();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [video?.id_video]);

    const handleSave = async () => {
        if (!video?.id_video) return;
        setSaving(true);
        try {
            await fetch(`${API_URL}?action=updateVideo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_video: video.id_video, titre, description }),
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } finally {
            setSaving(false);
        }
    };

    const handleGenerateChapters = async () => {
        if (!video?.id_video) {
            alert('Aucune vidéo liée. Uploadez d\'abord le fichier natif pour générer une transcription.');
            return;
        }
        setChaptersLoading(true);
        try {
            const fd = new FormData();
            fd.append('action', 'generate_chapters');
            fd.append('id_video', video.id_video);
            if (video.id_youtube) fd.append('id_youtube', video.id_youtube);
            const res = await fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd });
            const data = await res.json();
            if (data.chapters?.length) {
                setLocalChapters(data.chapters);
            } else {
                alert(data.error || 'Impossible de générer les chapitres.');
            }
        } catch (e) {
            alert('Erreur : ' + e.message);
        } finally {
            setChaptersLoading(false);
        }
    };

    const handleAiSuggest = async () => {
        if (!video?.id_video && !video?.id_youtube) {
            alert('Aucune source disponible. Uploadez d\'abord le fichier natif pour générer une transcription.');
            return;
        }
        setAiLoading(true);
        try {
            const formData = new FormData();
            formData.append('action', 'suggest_from_video');
            if (video.id_video) formData.append('id_video', video.id_video);
            if (video.id_youtube) formData.append('id_youtube', video.id_youtube);

            const contactList = participants
                .filter(p => p.contact?.id_contact)
                .map(p => ({
                    id_contact: p.contact.id_contact,
                    prenom: p.contact.prenom || '',
                    nom: p.contact.nom || '',
                    societe: p.contact.societe || '',
                    role: String(p.statut) === '27' ? 'animateur' : 'intervenant',
                }));
            if (contactList.length > 0) {
                formData.append('contacts', JSON.stringify(contactList));
            } else if (idContact) {
                formData.append('id_contact', idContact);
            }

            if (idPresta) formData.append('id_presta', idPresta);

            const res = await fetch(UPLOAD_API_URL, {
                method: 'POST',
                headers: { 'X-Api-Key': UPLOAD_API_KEY },
                body: formData,
            });
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); }
            catch { throw new Error(`Réponse non-JSON (HTTP ${res.status}) : ${text.slice(0, 300)}`); }
            if (data.ai) {
                setLocalAi(data.ai);
                if (data.ai.titre) setTitre(data.ai.titre);
                if (data.ai.description) setDescription(data.ai.description);
            }
            else alert(data.error || 'Erreur lors de la génération des suggestions IA.');
        } catch (e) {
            console.error('suggest_from_video error:', e);
            alert('Erreur : ' + (e.message || 'Erreur réseau lors de la génération IA.'));
        } finally {
            setAiLoading(false);
        }
    };

    const handleTranscribeFile = (file) => {
        if (!file || !file.type.startsWith('video/')) {
            alert('Veuillez sélectionner un fichier vidéo.');
            return;
        }
        setTranscribeStatus('uploading');
        setTranscribeProgress(0);

        const formData = new FormData();
        formData.append('action', 'transcribe_only');
        formData.append('video', file);
        if (video?.id_video) formData.append('id_video', video.id_video);

        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setTranscribeProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
            try {
                const data = JSON.parse(xhr.responseText);
                if (data.ia_transcription) {
                    setTranscribeStatus('done');
                } else {
                    setTranscribeStatus('error');
                    alert(data.error || 'Transcription échouée.');
                }
            } catch {
                setTranscribeStatus('error');
            }
        };
        xhr.onerror = () => setTranscribeStatus('error');
        xhr.open('POST', UPLOAD_API_URL);
        xhr.setRequestHeader('X-Api-Key', UPLOAD_API_KEY);
        xhr.send(formData);
    };

    return (
        <div className="mt-4 p-4 border-t dark:border-neutral-700 space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="font-bold text-gray-700 dark:text-neutral-200">Édition avancée</h4>
                <button
                    type="button"
                    onClick={handleAiSuggest}
                    disabled={aiLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-md disabled:opacity-50"
                >
                    <Sparkles size={13} />
                    {aiLoading ? 'Analyse en cours…' : 'Suggestion IA'}
                </button>
            </div>

            {/* Zone transcription fichier natif */}
            <div className="rounded-md border border-dashed border-gray-300 dark:border-neutral-700 p-3 bg-gray-50 dark:bg-neutral-800">
                <p className="text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-2">Transcription depuis le fichier natif</p>
                {transcribeStatus === 'idle' && (
                    <>
                        <input ref={transcribeInputRef} type="file" accept="video/*" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleTranscribeFile(f); }} />
                        <button type="button" onClick={() => transcribeInputRef.current?.click()}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 dark:text-neutral-200 bg-white dark:bg-neutral-700 border border-gray-300 dark:border-neutral-600 hover:bg-gray-100 dark:hover:bg-neutral-600 rounded-md">
                            <UploadCloud size={14} />
                            Ajouter le fichier natif pour transcription
                        </button>
                    </>
                )}
                {transcribeStatus === 'uploading' && (
                    <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs text-e2o-500">
                            <SpinnerIcon size={13} className="animate-spin" />
                            <span>Envoi & transcription… {transcribeProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-neutral-700 rounded-full h-1.5">
                            <div className="bg-e2o-500 h-1.5 rounded-full transition-all" style={{ width: `${transcribeProgress}%` }} />
                        </div>
                    </div>
                )}
                {transcribeStatus === 'done' && (
                    <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs text-green-700"><CheckCircle size={13} />Transcription enregistrée — cliquez sur "Suggestion IA"</span>
                        <button type="button" onClick={() => setTranscribeStatus('idle')} className="text-xs text-gray-400 hover:underline">Recommencer</button>
                    </div>
                )}
                {transcribeStatus === 'error' && (
                    <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs text-red-600"><XCircle size={13} />Échec</span>
                        <button type="button" onClick={() => setTranscribeStatus('idle')} className="text-xs text-gray-400 hover:underline">Réessayer</button>
                    </div>
                )}
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">Titre</label>
                <input
                    type="text"
                    className="w-full p-2 border dark:border-neutral-700 rounded text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white"
                    value={titre}
                    onChange={e => setTitre(e.target.value)}
                />
                {localAi?.titre && (
                    <div className="flex items-start gap-2 mt-1 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-2">
                        <Sparkles size={13} className="text-yellow-500 mt-0.5 shrink-0" />
                        <span className="text-xs text-gray-700 dark:text-neutral-200 flex-1">{localAi.titre}</span>
                        <button type="button" onClick={() => setTitre(localAi.titre)} className="text-xs text-e2o-500 dark:text-e2o-400 hover:underline shrink-0">Appliquer</button>
                    </div>
                )}
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">Description</label>
                <textarea
                    className="w-full p-2 border dark:border-neutral-700 rounded text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white"
                    rows={4}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                />
                {localAi?.description && (
                    <div className="flex items-start gap-2 mt-1 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-2">
                        <Sparkles size={13} className="text-yellow-500 mt-0.5 shrink-0" />
                        <span className="text-xs text-gray-700 dark:text-neutral-200 flex-1 line-clamp-3">{localAi.description}</span>
                        <button type="button" onClick={() => setDescription(localAi.description)} className="text-xs text-e2o-500 dark:text-e2o-400 hover:underline shrink-0">Appliquer</button>
                    </div>
                )}
            </div>

            {/* ── Chapitres YouTube ── */}
            <div>
                <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-gray-500 dark:text-neutral-400">Chapitres YouTube</label>
                    <button
                        type="button"
                        onClick={handleGenerateChapters}
                        disabled={chaptersLoading || !video?.id_video}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/40 rounded disabled:opacity-40"
                    >
                        <Sparkles size={11} />
                        {chaptersLoading ? 'Génération…' : 'Générer'}
                    </button>
                </div>
                {localChapters?.length > 0 ? (
                    <div className="rounded border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800 divide-y divide-gray-100 dark:divide-neutral-700">
                        {localChapters.map((ch, i) => (
                            <div key={i} className="flex items-center gap-2 px-2.5 py-1.5">
                                <span className="text-xs font-mono text-e2o-500 dark:text-e2o-400 w-10 shrink-0">{ch.time}</span>
                                <span className="text-xs text-gray-700 dark:text-neutral-200">{ch.title}</span>
                            </div>
                        ))}
                        <div className="px-2.5 py-1.5">
                            <p className="text-xs text-gray-400 dark:text-neutral-500">Injectés en tête de description YouTube</p>
                        </div>
                    </div>
                ) : (
                    <p className="text-xs text-gray-400 dark:text-neutral-500 italic">
                        {video?.id_video ? 'Transcription requise — cliquez sur Générer' : 'Uploadez la vidéo pour activer les chapitres'}
                    </p>
                )}
            </div>

            <button
                type="button"
                onClick={handleSave}
                disabled={saving || !video?.id_video}
                className="w-full px-3 py-2 text-sm font-medium text-white bg-e2o-500 hover:bg-e2o-600 rounded-md disabled:opacity-50"
            >
                {saving ? 'Enregistrement…' : saved ? '✓ Enregistré' : 'Enregistrer'}
            </button>

            {video?.id_video && <VideoPostsSection idVideo={video.id_video} />}
        </div>
    );
}

const SOCIAL_ICONS = {
    linkedin: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-[#0A66C2]">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
    ),
    default: <Video size={14} className="text-gray-400" />,
};

const getSocialIcon = (url) => {
    if (!url) return SOCIAL_ICONS.default;
    if (url.includes('linkedin.com')) return SOCIAL_ICONS.linkedin;
    return SOCIAL_ICONS.default;
};

const VideoPostsSection = ({ idVideo }) => {
    const [posts, setPosts] = useState([]);
    const [newUrl, setNewUrl] = useState('');
    const [adding, setAdding] = useState(false);
    const [showInput, setShowInput] = useState(false);

    useEffect(() => {
        if (!idVideo) return;
        fetch(`${API_URL}?action=getVideosPosts&params=WHERE id_video=${idVideo}`)
            .then(r => r.json())
            .then(data => Array.isArray(data) ? setPosts(data) : setPosts([]));
    }, [idVideo]);

    const handleAdd = async () => {
        const url = newUrl.trim();
        if (!url || !idVideo) return;
        setAdding(true);
        try {
            const res = await fetch(`${API_URL}?action=createVideosPosts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_video: idVideo, url }),
            });
            const data = await res.json();
            if (data.id_video_post) {
                setPosts(p => [data, ...p]);
                setNewUrl('');
                setShowInput(false);
            }
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (id_video_post) => {
        await fetch(`${API_URL}?action=deleteVideosPosts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id_video_post }),
        });
        setPosts(p => p.filter(x => x.id_video_post !== id_video_post));
    };

    return (
        <div className="border-t dark:border-neutral-700 pt-3 mt-3">
            <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-500 dark:text-neutral-400">Posts réseaux sociaux</label>
                <button
                    type="button"
                    onClick={() => setShowInput(v => !v)}
                    className="text-xs text-e2o-500 hover:underline flex items-center gap-1"
                >
                    <Plus size={11} /> Ajouter
                </button>
            </div>

            {showInput && (
                <div className="flex gap-2 mb-2">
                    <input
                        type="url"
                        value={newUrl}
                        onChange={e => setNewUrl(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        placeholder="https://www.linkedin.com/posts/..."
                        autoFocus
                        className="flex-1 border border-gray-300 dark:border-neutral-700 rounded-md px-3 py-1.5 text-xs bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-e2o-400"
                    />
                    <button
                        type="button"
                        onClick={handleAdd}
                        disabled={adding || !newUrl.trim()}
                        className="shrink-0 px-3 py-1.5 text-xs font-medium text-white bg-e2o-500 rounded-md hover:bg-e2o-600 disabled:opacity-50"
                    >
                        {adding ? '…' : 'OK'}
                    </button>
                </div>
            )}

            {posts.length > 0 ? (
                <ul className="space-y-1.5">
                    {posts.map(post => (
                        <li key={post.id_video_post} className="flex items-center gap-2 group">
                            {getSocialIcon(post.url)}
                            <a
                                href={post.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 text-xs text-e2o-500 hover:underline truncate"
                            >
                                {post.url}
                            </a>
                            <button
                                type="button"
                                onClick={() => handleDelete(post.id_video_post)}
                                className="opacity-0 group-hover:opacity-100 text-gray-300 dark:text-neutral-600 hover:text-red-500 transition-opacity shrink-0"
                            >
                                <X size={12} />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                !showInput && <p className="text-xs text-gray-400 dark:text-neutral-500 italic">Aucun post enregistré</p>
            )}
        </div>
    );
};

const CONTACT_FIELDS = [
    { key: 'prenom', label: 'Prénom' },
    { key: 'nom', label: 'Nom' },
    { key: 'societe', label: 'Société' },
    { key: 'fonction', label: 'Fonction' },
    { key: 'mail', label: 'Email' },
    { key: 'port', label: 'Mobile' },
    { key: 'sn_linkedin', label: 'LinkedIn' },
    { key: 'web', label: 'Site web' },
    { key: 'photo', label: 'Photo' },
    { key: 'logo', label: 'Logo' },
];

const IMG_BASE = 'https://www.mlg-consulting.com/manager_cc/contacts/img_uploaded/';

const VisuelUploadField = ({ label, fieldName, idContact, currentValue, onUploaded }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [preview, setPreview] = useState(currentValue || '');

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setUploadError('');
        try {
            const fd = new FormData();
            fd.append('action', 'updateContactVisuel');
            fd.append('id_contact', idContact);
            fd.append(fieldName, file);
            const res = await fetch(API_URL + '?action=updateContactVisuel', { method: 'POST', body: fd });
            const data = await res.json();
            if (data.fileName) {
                setPreview(data.fileName);
                onUploaded(fieldName, data.fileName);
            } else {
                throw new Error('Réponse invalide');
            }
        } catch (err) {
            setUploadError('Erreur upload : ' + err.message);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const imgSrc = preview ? (preview.startsWith('http') ? preview : IMG_BASE + preview) : null;

    return (
        <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">{label}</label>
            <div className="flex items-center gap-3">
                {imgSrc
                    ? <img src={imgSrc} alt={label} className="w-12 h-12 rounded object-cover border border-gray-200 dark:border-neutral-700" />
                    : <div className="w-12 h-12 rounded bg-gray-100 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 flex items-center justify-center text-gray-300 dark:text-neutral-600"><User size={20} /></div>
                }
                <div className="flex-1">
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="px-3 py-1.5 text-xs border border-gray-300 dark:border-neutral-700 rounded-md hover:bg-gray-50 dark:hover:bg-neutral-800 dark:text-neutral-200 disabled:opacity-50"
                    >
                        {uploading ? 'Upload...' : 'Choisir un fichier'}
                    </button>
                    <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                    {uploadError && <p className="mt-1 text-xs text-red-500">{uploadError}</p>}
                </div>
            </div>
        </div>
    );
};

const ContactEditModal = ({ contact, onClose, onSaved }) => {
    const [form, setForm] = useState(
        Object.fromEntries(CONTACT_FIELDS.map(f => [f.key, contact?.[f.key] || '']))
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (key, value) => setForm(f => ({ ...f, [key]: value }));

    const handleLinkedinSearch = () => {
        const query = [form.prenom, form.nom, form.societe].filter(Boolean).join(' ');
        if (!query) return;
        window.open(
            `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(query)}`,
            '_blank',
            'noopener,noreferrer'
        );
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            const res = await fetch(`${API_URL}?action=updateContact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ formData: { id_contact: contact.id_contact, ...form } }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            onSaved({ ...contact, ...form });
            onClose();
        } catch (e) {
            setError('Erreur lors de la sauvegarde : ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">Éditer le contact</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"><X size={20} /></button>
                </div>

                <div className="space-y-3">
                    {CONTACT_FIELDS.map(({ key, label }) => (
                        <div key={key}>
                            <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">{label}</label>
                            {key === 'sn_linkedin' ? (
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={form[key]}
                                        onChange={e => handleChange(key, e.target.value)}
                                        placeholder="https://www.linkedin.com/in/..."
                                        className="flex-1 border border-gray-300 dark:border-neutral-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-e2o-400"
                                    />
                                    {!form[key] && (
                                        <button
                                            type="button"
                                            onClick={handleLinkedinSearch}
                                            className="shrink-0 flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-e2o-500 rounded-md hover:bg-e2o-600"
                                        >
                                            <Search size={13} />
                                            Rechercher
                                        </button>
                                    )}
                                </div>
                            ) : (key === 'photo' || key === 'logo') ? (
                                <VisuelUploadField
                                    label=""
                                    fieldName={key}
                                    idContact={contact.id_contact}
                                    currentValue={form[key]}
                                    onUploaded={(field, fileName) => handleChange(field, fileName)}
                                />
                            ) : (
                                <input
                                    type="text"
                                    value={form[key]}
                                    onChange={e => handleChange(key, e.target.value)}
                                    className="w-full border border-gray-300 dark:border-neutral-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-e2o-400"
                                />
                            )}
                        </div>
                    ))}
                </div>

                {error && <p className="mt-3 text-xs text-red-500">{error}</p>}

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-neutral-300 border border-gray-300 dark:border-neutral-700 rounded-md hover:bg-gray-50 dark:hover:bg-neutral-800">Annuler</button>
                    <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm text-white bg-e2o-500 rounded-md hover:bg-e2o-600 disabled:opacity-50">
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const LANG_INFO = {
    fr: { flag: '🇫🇷', label: 'Français' },
    en: { flag: '🇬🇧', label: 'English' },
};

const LangueBadge = ({ langue }) => {
    const info = LANG_INFO[langue] || LANG_INFO.fr;
    return (
        <span
            title="Langue d'interview"
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:text-neutral-200 bg-gray-100 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-full shrink-0"
        >
            <span className="text-sm leading-none">{info.flag}</span>
            Langue d'interview : {info.label}
        </span>
    );
};

/**
 * Extrait l'ID d'une vidéo YouTube à partir de différentes formes d'URL.
 */
const extractYoutubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};

const withAlpha = (color, alpha) => {
    if (!color || !color.trim()) return null;
    const c = color.trim();
    const hex6 = /^#([0-9A-Fa-f]{6})$/;
    const hex3 = /^#([0-9A-Fa-f]{3})$/;
    if (hex6.test(c)) {
        const r = parseInt(c.slice(1, 3), 16);
        const g = parseInt(c.slice(3, 5), 16);
        const b = parseInt(c.slice(5, 7), 16);
        return `rgba(${r},${g},${b},${alpha})`;
    }
    if (hex3.test(c)) {
        const r = parseInt(c[1] + c[1], 16);
        const g = parseInt(c[2] + c[2], 16);
        const b = parseInt(c[3] + c[3], 16);
        return `rgba(${r},${g},${b},${alpha})`;
    }
    return c;
};

/**
 * Carte vidéo synthétique — détails et actions dans VideoEditPage.
 */ const VideoCard = ({ data, isAdmin, onVideoUploaded, onDeleted, youtubeOrgs = [], profile }) => {
    const { contacts = [], video, presta, conf_event } = data;
    const youtubeId = video?.id_video?.id_youtube || (video?.video_url ? extractYoutubeId(video.video_url) : null);
    const { currentEvent } = useContext(EventContext);
    const { apiUrlTranslation } = useApiUrlTranslation();

    const [localContacts, setLocalContacts] = useState(contacts);
    const [editingContact, setEditingContact] = useState(null);
    const [ytStatus, setYtStatus] = useState(null);
    const [nativeStatus, setNativeStatus] = useState(null);
    const [selectedOrgId, setSelectedOrgId] = useState(1);
    const [showParams, setShowParams] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);

    const idVideo = video?.id_video?.id_video;
    const idConfEvent = conf_event?.id_conf_event;

    // Plans d'action (cycle_lang)
    const [cycleLinks, setCycleLinks] = useState([]);
    const [cycleQuery, setCycleQuery] = useState('');
    const [cycleResults, setCycleResults] = useState([]);
    const [cycleSearching, setCycleSearching] = useState(false);
    const [cycleAdding, setCycleAdding] = useState(null);
    const [cycleRemoving, setCycleRemoving] = useState(null);
    const cycleSearchTimeout = useRef(null);

    useEffect(() => {
        if (!idVideo) return;
        fetch(`${API_URL}?action=getVideosCycleLangList&filter=${encodeURIComponent(`WHERE id_video = ${idVideo}`)}`)
            .then(r => r.json())
            .then(d => setCycleLinks(Array.isArray(d) ? d : []))
            .catch(() => { });
    }, [idVideo]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCycleSearch = (val) => {
        setCycleQuery(val);
        setCycleResults([]);
        clearTimeout(cycleSearchTimeout.current);
        if (val.trim().length < 2) return;
        cycleSearchTimeout.current = setTimeout(async () => {
            setCycleSearching(true);
            try {
                const filter = encodeURIComponent(`nom_cycle_lang LIKE '%${val.trim()}%' AND `);
                const res = await fetch(`${apiUrlTranslation}?action=getCycleLang&filter=${filter}&get_children=0&get_parents=0`);
                const d = await res.json();
                setCycleResults(Array.isArray(d) ? d.slice(0, 6) : []);
            } catch { } finally { setCycleSearching(false); }
        }, 350);
    };

    const linkedCycleIds = new Set(cycleLinks.map(l => String(l.id_cycle_lang?.id_cycle_lang || l.id_cycle_lang)));

    const handleCycleAdd = async (cycle) => {
        if (!idVideo || linkedCycleIds.has(String(cycle.id_cycle_lang))) return;
        setCycleAdding(cycle.id_cycle_lang);
        try {
            await fetch(`${API_URL}?action=createVideosCycleLang`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_video: idVideo, id_cycle_lang: cycle.id_cycle_lang }),
            });
            const res = await fetch(`${API_URL}?action=getVideosCycleLangList&filter=${encodeURIComponent(`WHERE id_video = ${idVideo}`)}`);
            const d = await res.json();
            setCycleLinks(Array.isArray(d) ? d : []);
            setCycleQuery('');
            setCycleResults([]);
        } finally { setCycleAdding(null); }
    };

    const handleCycleRemove = async (link) => {
        const linkId = link.id_videos_cycle_lang;
        setCycleRemoving(linkId);
        try {
            await fetch(`${API_URL}?action=deleteVideosCycleLang`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_videos_cycle_lang: linkId }),
            });
            setCycleLinks(prev => prev.filter(l => l.id_videos_cycle_lang !== linkId));
        } finally { setCycleRemoving(null); }
    };

    useEffect(() => {
        if (!youtubeOrgs.length) return;
        const idEventLigne = currentEvent?.event_ligne?.id_event_ligne;
        if (!idEventLigne) {
            setSelectedOrgId(youtubeOrgs[0].organisation_id);
            return;
        }
        const fd = new FormData();
        fd.append('action', 'resolve_event_organisation');
        fd.append('id_event_ligne', idEventLigne);
        fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
            .then(r => r.json())
            .then(d => {
                const eventOrg = youtubeOrgs.find(o => o.organisation_id === (d.organisation_id ?? 1));
                setSelectedOrgId(eventOrg ? eventOrg.organisation_id : youtubeOrgs[0].organisation_id);
            })
            .catch(() => setSelectedOrgId(youtubeOrgs[0].organisation_id));
    }, [youtubeOrgs]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!youtubeId) return;
        const fd = new FormData();
        fd.append('action', 'get_youtube_status');
        fd.append('id_youtube', youtubeId);
        fd.append('organisation_id', currentEvent?.organisation_id ?? 1);
        fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
            .then(r => r.text())
            .then(raw => {
                if (!raw || !raw.trim()) { setYtStatus({ _unavailable: true }); return; }
                try { setYtStatus(JSON.parse(raw)); }
                catch { setYtStatus({ _unavailable: true }); }
            })
            .catch(() => setYtStatus({ _unavailable: true }));
    }, [youtubeId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!idVideo) return;
        const fd = new FormData();
        fd.append('action', 'get_native_status');
        fd.append('id_video', idVideo);
        fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
            .then(r => r.text())
            .then(raw => {
                if (!raw || !raw.trim()) return;
                try { setNativeStatus(JSON.parse(raw)); } catch { }
            })
            .catch(() => { });
    }, [idVideo]); // eslint-disable-line react-hooks/exhaustive-deps

    const thumbnail = video?.id_video?.thumbnail_large_url || video?.id_video?.thumbnail_medium_url
        || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null);

    const handleDeleteSlot = async () => {
        setDeleting(true);
        try {
            // 1. Supprimer la vidéo YouTube si elle existe
            if (youtubeId) {
                const fd = new FormData();
                fd.append('action', 'delete_youtube_video');
                fd.append('id_youtube', youtubeId);
                fd.append('organisation_id', currentEvent?.organisation_id ?? 1);
                await fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd });
            }
            // 2. Supprimer la vidéo en BDD (le trigger MySQL cascade le reste)
            if (idVideo) {
                await fetch(`${API_URL}?action=deleteVideo`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id_video: idVideo }),
                });
            }
            // 3. Supprimer le conferencier lié au créneau
            if (idConfEvent) {
                await fetch(`${API_URL}?action=deleteConferencier`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id_conf_event: idConfEvent }),
                });
            }
            // 4. Supprimer le créneau conf_event
            if (idConfEvent) {
                await fetch(`${API_URL}?action=deleteConfEvent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id_conf_event: idConfEvent }),
                });
            }
            onDeleted?.(idConfEvent);
        } catch (err) {
            console.error('deleteSlot error:', err);
            setDeleting(false);
            setConfirmDelete(false);
        }
    };

    return (
        <div className="bg-white dark:bg-neutral-900 rounded-xl overflow-hidden border border-gray-100 dark:border-neutral-800 shadow-sm flex flex-col">
            {/* Thumbnail compact */}
            <div className="relative w-full h-24 bg-gray-100 dark:bg-neutral-800 shrink-0">
                {thumbnail ? (
                    youtubeId ? (
                        <a href={`https://www.youtube.com/watch?v=${youtubeId}`} target="_blank" rel="noopener noreferrer" className="block w-full h-full group">
                            <img src={thumbnail} alt={presta?.presta_nom || ''} className="w-full h-full object-cover" />
                            <span className="absolute inset-0 flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity">
                                <svg viewBox="0 0 68 48" className="w-10 h-7 drop-shadow-lg" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M66.5 7.7c-.8-2.9-2.9-5.1-5.8-5.9C55.8.5 34 .5 34 .5s-21.8 0-26.7 1.3c-2.9.8-5 3-5.8 5.9C.2 12.7.2 24 .2 24s0 11.3 1.3 16.3c.8 2.9 2.9 5.1 5.8 5.9C12.2 47.5 34 47.5 34 47.5s21.8 0 26.7-1.3c2.9-.8 5-3 5.8-5.9C67.8 35.3 67.8 24 67.8 24s0-11.3-1.3-16.3z" fill="#FF0000" fillOpacity="0.9" />
                                    <path d="M27 33.5l18-9.5-18-9.5v19z" fill="white" />
                                </svg>
                            </span>
                        </a>
                    ) : (
                        <img src={thumbnail} alt={presta?.presta_nom || ''} className="w-full h-full object-cover" />
                    )
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-neutral-700">
                        <Film size={28} />
                    </div>
                )}
            </div>

            {/* Body */}
            <div className="p-3 flex-1 flex flex-col gap-2">
                {/* Title + edit link */}
                <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white leading-snug line-clamp-2">{presta?.presta_nom || 'Présentation'}</h3>
                    {isAdmin && (
                        <div className="flex items-center gap-1.5 shrink-0">
                            {(idVideo || idConfEvent) && (
                                <Link
                                    to={`/events/${currentEvent?.id_event}/video-edit/${idVideo || idConfEvent}`}
                                    className="flex items-center gap-1.5 text-xs font-medium text-e2o-500 dark:text-e2o-400 bg-e2o-50 dark:bg-e2o-900/20 border border-e2o-200 dark:border-e2o-800 hover:bg-e2o-100 dark:hover:bg-e2o-900/40 rounded-md px-2.5 py-1 transition-colors"
                                >
                                    <PenSquare size={11} />
                                    Éditer
                                </Link>
                            )}
                            {/* bouton paramètres masqué — disponible dans VideoEditPage */}
                        </div>
                    )}
                </div>

                <LangueBadge langue={presta?.langue_enregistrement} />

                {/* Contacts */}
                {localContacts.length > 0 && (
                    <div className="space-y-2">
                        {localContacts.map(lc => (
                            <div key={lc.id_contact} className="flex items-start gap-2">
                                {lc.photos?.small ? (
                                    <img src={lc.photos.small} alt={`${lc.prenom} ${lc.nom}`} className="w-8 h-8 rounded-full object-cover ring-1 ring-gray-200 dark:ring-neutral-700 shrink-0 mt-0.5" />
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center shrink-0 mt-0.5">
                                        <User size={14} className="text-gray-400 dark:text-neutral-500" />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{lc.prenom} {lc.nom}</p>
                                    <p className="text-xs text-gray-500 dark:text-neutral-400 truncate mt-0.5">{lc.societe}{lc.pays ? `  -  ${lc.flag} ${lc.pays}` : ''}</p>
                                    {(lc.sn_linkedin || isAdmin) && (
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {lc.sn_linkedin && (
                                                <a href={lc.sn_linkedin} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="flex items-center justify-center w-5 h-5 rounded bg-[#0A66C2] hover:bg-[#004182] transition-colors shrink-0">
                                                    <svg viewBox="0 0 24 24" fill="white" className="w-3 h-3">
                                                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                                    </svg>
                                                </a>
                                            )}
                                            {isAdmin && (
                                                <button onClick={() => setEditingContact(lc)} className="text-xs text-e2o-500 dark:text-e2o-400 hover:underline whitespace-nowrap">
                                                    Éditer le contact
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {editingContact && (
                    <ContactEditModal contact={editingContact} onClose={() => setEditingContact(null)} onSaved={updated => setLocalContacts(prev => prev.map(c => c.id_contact === updated.id_contact ? { ...c, ...updated } : c))} />
                )}

                {/* Slot */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs text-gray-400 dark:text-neutral-500">sessionId:{idConfEvent} · {conf_event?.conf_event_date} {conf_event?.heure_debut}–{conf_event?.heure_fin}{idVideo ? ` - videoId:${idVideo}` : ''}{conf_event?.conf_event_lang?.cel_titre ? ` · ${conf_event.conf_event_lang.cel_titre}` : ''}</p>
                    <button
                        onClick={() => setShowShareModal(true)}
                        className="shrink-0 flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-e2o-600 dark:text-e2o-400 border border-e2o-200 dark:border-e2o-800 hover:bg-e2o-50 dark:hover:bg-e2o-950/30 rounded-md transition-colors"
                    >
                        <ExternalLink size={11} />
                        Partager le lien
                    </button>
                </div>

                {/* YouTube status badge */}
                {youtubeId && (
                    <div className="flex flex-wrap gap-1.5">
                        {!ytStatus && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse inline-block" /> YouTube…
                            </span>
                        )}
                        {ytStatus?._unavailable && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> En ligne
                            </span>
                        )}
                        {ytStatus && !ytStatus._unavailable && ytStatus.upload_status === 'processed' && ytStatus.privacy_status === 'public' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Public
                            </span>
                        )}
                        {ytStatus && !ytStatus._unavailable && ytStatus.upload_status === 'processed' && ytStatus.privacy_status === 'unlisted' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 inline-block" /> En attente d'approbation
                            </span>
                        )}
                        {ytStatus && !ytStatus._unavailable && ytStatus.upload_status === 'processed' && ytStatus.privacy_status === 'private' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-neutral-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" /> Privé
                            </span>
                        )}
                        {ytStatus && !ytStatus._unavailable && ytStatus.upload_status && ytStatus.upload_status !== 'processed' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-e2o-100 dark:bg-e2o-900/30 text-e2o-600 dark:text-e2o-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-e2o-400 animate-pulse inline-block" /> En traitement
                            </span>
                        )}
                        {ytStatus && !ytStatus._unavailable && ytStatus.status === 'not_found' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" /> Introuvable sur YouTube
                            </span>
                        )}
                    </div>
                )}

                {/* Rétention du fichier natif */}
                {nativeStatus?.has_native && (
                    <div className="flex flex-wrap gap-1.5">
                        {nativeStatus.is_expired ? (
                            <span title="Expiration du lien de téléchargement de la vidéo native" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" /> Lien natif expiré
                            </span>
                        ) : nativeStatus.days_remaining !== null && nativeStatus.days_remaining <= 5 ? (
                            <span title="Expiration du lien de téléchargement de la vidéo native" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 inline-block" /> Lien natif expire J-{nativeStatus.days_remaining}
                            </span>
                        ) : (
                            <span title="Expiration du lien de téléchargement de la vidéo native" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Lien natif expire J-{nativeStatus.days_remaining}
                            </span>
                        )}
                        {nativeStatus.download_url && (
                            <a
                                href={nativeStatus.download_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-e2o-600 dark:text-e2o-400 border border-e2o-200 dark:border-e2o-800 hover:bg-e2o-50 dark:hover:bg-e2o-950/30 transition-colors"
                            >
                                <Download size={11} /> Télécharger le natif
                            </a>
                        )}
                    </div>
                )}

                {/* Plans d'action (cycle_lang) */}
                {idVideo && (
                    <div className="space-y-1">
                        <p className="text-[10px] font-semibold text-gray-400 dark:text-neutral-500 uppercase tracking-wide">Plans d'action</p>
                        {cycleLinks.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {cycleLinks.map(link => {
                                    const cl = link.id_cycle_lang ?? link;
                                    const label = cl?.nom_cycle_lang || cl?.id_cycle_lang || link.id_cycle_lang;
                                    const logo = cl?.logo_better?.tiny;
                                    const color = cl?.cycle_lang_color_1;
                                    const bgColor = withAlpha(color, 0.15);
                                    const borderColor = withAlpha(color, 0.45);
                                    const linkId = link.id_videos_cycle_lang;
                                    return (
                                        <span
                                            key={linkId}
                                            className={`inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full text-xs border font-medium${!bgColor ? ' bg-e2o-100 dark:bg-e2o-900/30 text-e2o-700 dark:text-e2o-300 border-e2o-200 dark:border-e2o-800' : ''}`}
                                            style={bgColor ? { backgroundColor: bgColor, borderColor, color } : undefined}
                                        >
                                            {logo && <img src={logo} alt="" className="w-4 h-4 rounded-full object-contain bg-white shrink-0" />}
                                            {label}
                                            <button
                                                onClick={() => handleCycleRemove(link)}
                                                disabled={cycleRemoving === linkId}
                                                className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity disabled:opacity-30"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        <div className="relative">
                            <input
                                type="text"
                                value={cycleQuery}
                                onChange={e => handleCycleSearch(e.target.value)}
                                placeholder="Lier un plan d'action…"
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-e2o-400"
                            />
                            {cycleSearching && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">…</span>
                            )}
                            {cycleResults.length > 0 && (
                                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-md shadow-xl max-h-40 overflow-y-auto">
                                    {cycleResults.map(cycle => {
                                        const alreadyLinked = linkedCycleIds.has(String(cycle.id_cycle_lang));
                                        return (
                                            <button
                                                key={cycle.id_cycle_lang}
                                                disabled={alreadyLinked || cycleAdding === cycle.id_cycle_lang}
                                                onClick={() => handleCycleAdd(cycle)}
                                                className="w-full text-left px-3 py-2 text-xs text-gray-800 dark:text-neutral-100 hover:bg-gray-100 dark:hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-default transition-colors flex items-center gap-2"
                                                style={withAlpha(cycle.cycle_lang_color_1, 1) ? { borderLeft: `3px solid ${cycle.cycle_lang_color_1}` } : undefined}
                                            >
                                                {cycle.logo_better?.tiny && (
                                                    <img src={cycle.logo_better.tiny} alt="" className="w-5 h-5 rounded object-contain shrink-0 bg-white" />
                                                )}
                                                <span className="truncate">{cycle.nom_cycle_lang}</span>
                                                {alreadyLinked && <span className="ml-auto text-e2o-500 shrink-0">✓</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Upload + org selector */}
                {isAdmin && (
                    <div className="space-y-2 pt-1">
                        {youtubeOrgs.length > 1 && (
                            <select
                                value={selectedOrgId}
                                onChange={e => setSelectedOrgId(Number(e.target.value))}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-gray-800 dark:text-white"
                            >
                                {youtubeOrgs.map(org => (
                                    <option key={org.organisation_id} value={org.organisation_id}>
                                        {org.label}{org.is_default ? ' (défaut)' : ''}
                                    </option>
                                ))}
                            </select>
                        )}
                        <DragDropVideoUpload
                            compact
                            idConfEvent={idConfEvent}
                            idContact={localContacts[0]?.id_contact}
                            title={`${localContacts.map(c => `${c.prenom} ${c.nom}`).join(', ')} - ${presta?.punchline || ''}`}
                            idEvent={currentEvent?.id_event}
                            organisationId={selectedOrgId}
                            lang={presta?.langue_enregistrement || 'fr'}
                            onUploadComplete={({ id_video, youtube_id, id_conf_event: idCE, ai, chapters }) => {
                                onVideoUploaded(idCE ?? idConfEvent, id_video, youtube_id, ai, chapters);
                            }}
                        />
                    </div>
                )}

                {/* Zone de danger */}
                {showParams && (
                    <div className="mt-1 rounded-lg border border-red-200 dark:border-red-900/60 overflow-hidden">
                        <div className="px-3 py-2 bg-red-50 dark:bg-red-950/30 flex items-center gap-2">
                            <AlertTriangle size={13} className="text-red-500 shrink-0" />
                            <span className="text-xs font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide">Zone de danger</span>
                        </div>
                        <div className="px-3 py-3 flex items-center justify-between gap-3 bg-white dark:bg-neutral-900">
                            <div>
                                <p className="text-xs font-medium text-gray-800 dark:text-neutral-200">Supprimer ce slot</p>
                                <p className="text-[11px] text-gray-500 dark:text-neutral-400 mt-0.5">Vidéo, intervenants et publication YouTube supprimés définitivement.</p>
                            </div>
                            <button
                                onClick={() => setConfirmDelete(true)}
                                className="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-md transition-colors"
                            >
                                <Trash2 size={11} />
                                Supprimer
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showShareModal && (
                <SendSlotLinkModal
                    currentEvent={currentEvent}
                    onClose={() => setShowShareModal(false)}
                    profile={profile}
                    idConfEvent={idConfEvent}
                />
            )}

            {/* Modale de confirmation */}
            {confirmDelete && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl w-full max-w-sm border border-red-200 dark:border-red-900/60">
                        <div className="p-5 space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-950/50 flex items-center justify-center shrink-0">
                                    <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">Supprimer ce slot ?</h3>
                                    <p className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">{localContacts.map(c => `${c.prenom} ${c.nom}`).join(', ') || 'Slot sans intervenant'}</p>
                                </div>
                            </div>
                            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-lg px-3 py-2.5">
                                <p className="text-xs text-red-700 dark:text-red-300 leading-relaxed">
                                    <strong>Attention, cette opération est irréversible.</strong> Toutes les informations liées à ce slot seront définitivement perdues : vidéo, transcription, intervenants, publication YouTube.
                                </p>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <button
                                    onClick={() => setConfirmDelete(false)}
                                    disabled={deleting}
                                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg disabled:opacity-50 transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleDeleteSlot}
                                    disabled={deleting}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
                                >
                                    {deleting ? <SpinnerIcon size={13} className="animate-spin" /> : <Trash2 size={13} />}
                                    {deleting ? 'Suppression…' : 'Supprimer définitivement'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SendSlotLinkModal = ({ currentEvent, onClose, profile, idConfEvent = null, initialContact = null, initialConferencierId = null }) => {
    const { apiUrlTranslation } = useApiUrlTranslation();
    const baseUrl = `${window.location.origin}/events/${currentEvent?.id_event}/self-register-slot`;

    // Contact search
    const [query, setQuery] = useState(initialContact ? `${initialContact.prenom} ${initialContact.nom}` : '');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedContact, setSelectedContact] = useState(initialContact);
    const searchTimeout = useRef(null);

    // Email send
    const [mail, setMail] = useState(initialContact?.mail || '');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [copied, setCopied] = useState(false);

    // Rôle destinataire
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(143);

    // Dernière presta du contact sélectionné — seul id_presta sert (le formulaire cible
    // recharge punchline/video_url/video_hosted/presta_visuel lui-même via getPrestaList)
    const [contactPrestaId, setContactPrestaId] = useState(null);

    // id_conferencier existant du contact sur cet événement — indispensable pour que le lien
    // généré fasse passer SelfRegisterSlot en mode "mise à jour" (sinon chaque envoi/soumission
    // recrée un conf_event + conferencier au lieu de réutiliser le rendez-vous déjà pris).
    // Si l'appelant le connaît déjà (ex: ligne de tableau déjà associée à un conferencier),
    // on l'utilise directement et on évite le fetch de recherche.
    const [contactConferencierId, setContactConferencierId] = useState(initialConferencierId);

    useEffect(() => {
        fetch(`${API_URL}?action=getEventContactTypeList&filter=WHERE id_event_contact_type IN(143, 259, 92)`)
            .then(r => r.json())
            .then(data => { if (Array.isArray(data)) setRoles(data); })
            .catch(() => { });
    }, []);

    const fetchContactPresta = (id_contact) => {
        fetch(`${API_URL}?action=getPrestaList&params=${encodeURIComponent(`WHERE id_contact=${id_contact} ORDER BY id_presta DESC LIMIT 1`)}`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data) && data[0]?.id_presta) setContactPrestaId(data[0].id_presta);
            })
            .catch(() => { });
    };

    const fetchContactConferencier = (id_contact) => {
        fetch(`${API_URL}?action=getPartenairesLight&params=${encodeURIComponent(`AND cf.id_contact=${id_contact} AND cf.id_event=${currentEvent?.id_event} ORDER BY cf.id_conferencier DESC LIMIT 1`)}`)
            .then(r => r.json())
            .then(data => {
                const row = Array.isArray(data) ? data[0] : null;
                if (row?.id_conferencier) setContactConferencierId(row.id_conferencier);
            })
            .catch(() => { });
    };

    // Contact pré-sélectionné (ex: bouton "Partager" depuis une ligne) : sa presta et son
    // conferencier n'ont pas encore été chargés
    useEffect(() => {
        if (initialContact?.id_contact) {
            fetchContactPresta(initialContact.id_contact);
            if (!initialConferencierId) fetchContactConferencier(initialContact.id_contact);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Historique des envois
    const { logs: sendHistory, refresh: refreshHistory } = useActivityLog({
        id_event: currentEvent?.id_event,
        action_type: 'slot_link_sent',
        enabled: !!currentEvent?.id_event,
    });

    // URL avec rôle + créneau fixe (si idConfEvent) — id_contact/id_presta suffisent : la page
    // self-register-slot recharge elle-même toutes les infos du contact et de sa presta via ces
    // ids (getContact / getPrestaList). On évite ainsi de faire transiter des données personnelles
    // (nom, email, téléphone, LinkedIn…) en clair dans l'URL.
    const selfRegisterUrl = (() => {
        const params = new URLSearchParams({ id_event_contact_type: selectedRole });
        if (idConfEvent) params.set('id_conf_event', idConfEvent);
        if (selectedContact) {
            params.set('id_contact', selectedContact.id_contact);
            if (contactPrestaId) params.set('id_presta', contactPrestaId);
            if (contactConferencierId) params.set('id_conferencier', contactConferencierId);
        }
        return `${baseUrl}?${params.toString()}`;
    })();

    const selectedRoleLabel = roles.find(r => parseInt(r.id_event_contact_type, 10) === selectedRole)?.libelle || 'participant';

    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(selfRegisterUrl)}`;

    const handleQueryChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        setSelectedContact(null);
        setContactPrestaId(null);
        setContactConferencierId(null);
        setMail('');
        setSent(false);
        setCopied(false);
        clearTimeout(searchTimeout.current);
        if (val.length < 2) { setResults([]); return; }
        searchTimeout.current = setTimeout(async () => {
            setSearching(true);
            try {
                const words = val.trim().split(/\s+/).filter(Boolean);
                const conditions = words.map(w =>
                    `(nom LIKE '%${w}%' OR prenom LIKE '%${w}%' OR societe LIKE '%${w}%' OR mail LIKE '%${w}%')`
                ).join(' AND ');
                const sql = `WHERE ${conditions} ORDER  BY certified desc, nom LIMIT 8`;
                const res = await fetch(`${apiUrlTranslation}?action=getContactList&params=${encodeURIComponent(sql)}`);
                const data = await res.json();
                setResults(Array.isArray(data) ? data : []);
            } finally {
                setSearching(false);
            }
        }, 400);
    };

    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        setQuery(`${contact.prenom} ${contact.nom}`);
        setMail(contact.mail || '');
        setResults([]);
        setSent(false);
        setCopied(false);
        setContactPrestaId(null);
        setContactConferencierId(null);
        fetchContactPresta(contact.id_contact);
        fetchContactConferencier(contact.id_contact);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(selfRegisterUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleDownloadQr = () => {
        const a = document.createElement('a');
        a.href = `https://api.qrserver.com/v1/create-qr-code/?size=1000x1000&format=png&data=${encodeURIComponent(selfRegisterUrl)}`;
        a.download = `qrcode-inscription-${currentEvent?.id_event}.png`;
        a.target = '_blank';
        a.click();
    };

    const handleSend = async () => {
        if (!mail) return;
        setSending(true);
        try {
            await fetch(`${API_URL}?action=sendEmailNotification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: mail,
                    mobile: '0698969096',
                    subject: `Invitation ${selectedRoleLabel} — ${currentEvent?.nom}`,
                    body: [
                        `<p>Bonjour,</p>`,
                        `<p>Vous êtes invité(e) à vous inscrire en tant que <strong>${selectedRoleLabel}</strong> lors de l'événement <strong>${currentEvent?.nom}</strong>.</p>`,
                        `<p>Cliquez sur le bouton ci-dessous pour remplir le formulaire d'inscription :</p>`,
                        `<p><a href="${selfRegisterUrl}" style="display:inline-block;padding:10px 24px;background-color:#2563eb;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;font-size:14px;">Je m'inscris</a></p>`,
                        `<p style="color:#6b7280;font-size:12px;">Ou copiez ce lien dans votre navigateur :<br/><a href="${selfRegisterUrl}" style="color:#2563eb;">${selfRegisterUrl}</a></p>`,
                        `<p>À bientôt !</p>`,
                    ].join(''),

                }),
            });

            // Mise à jour immédiate de l'interface
            setSent(true);
            setMail('');
            await logActivity({
                action_type: 'slot_link_sent',
                id_contact_emitter: profile?.id_contact ?? null,
                id_contact_receiver: selectedContact?.id_contact ?? null,
                id_event: currentEvent?.id_event ?? null,
                metadata: {
                    channel: 'email',
                    email: mail,
                    url: selfRegisterUrl,
                    id_event_contact_type: selectedRole,
                    role: selectedRoleLabel,
                    receiver: selectedContact
                        ? `${selectedContact.prenom} ${selectedContact.nom}`
                        : mail,
                },
            });
            refreshHistory();

            // Reset du message de confirmation après 3 secondes
            setTimeout(() => {
                setSent(false);
            }, 3000);

        } catch (error) {
            console.error('Erreur lors de l\'envoi de l\'email:', error);
            alert('Erreur lors de l\'envoi de l\'email. Veuillez réessayer.');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 dark:text-white">Partager le lien d'inscription</h2>
                        {idConfEvent && <p className="text-xs text-e2o-500 dark:text-e2o-400 mt-0.5">Créneau #{idConfEvent} — le destinataire s'inscrira sur ce slot</p>}
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"><X size={20} /></button>
                </div>

                {/* Recherche de contact existant */}
                <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">Rechercher un contact existant</label>
                    <div className="relative">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500" />
                        <input
                            type="text"
                            value={query}
                            onChange={handleQueryChange}
                            placeholder="Nom, prénom, société ou email…"
                            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-e2o-400"
                        />
                        {searching && <SpinnerIcon size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-e2o-500 animate-spin" />}
                    </div>
                    {results.length > 0 && (
                        <ul className="mt-1 border border-gray-200 dark:border-neutral-700 rounded-lg overflow-hidden shadow-sm">
                            {results.map(c => (
                                <li key={c.id_contact}>
                                    <button
                                        onClick={() => handleSelectContact(c)}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-e2o-50 dark:hover:bg-neutral-800 flex items-center gap-3 border-b border-gray-100 dark:border-neutral-800 last:border-0"
                                    >
                                        {c.photo ? (
                                            <img src={c.photos.tiny} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                                        ) : (
                                            <div className="w-7 h-7 rounded-full bg-e2o-100 dark:bg-e2o-900/30 flex items-center justify-center shrink-0">
                                                <User size={13} className="text-e2o-600 dark:text-e2o-400" />
                                            </div>
                                        )}

                                        <div className="min-w-0">
                                            <p className="font-medium text-gray-800 dark:text-white truncate">{c.prenom} {c.nom}</p>
                                            <p className="text-xs text-gray-500 dark:text-neutral-400 truncate">{c.societe}{c.mail ? ` · ${c.mail}` : ''}</p>
                                        </div>
                                        {c.certified}

                                        {
                                            c.certified === "1" && (
                                                <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> Certifié
                                                </span>
                                            )
                                        }
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {selectedContact && (
                        <p className="mt-1.5 text-xs text-e2o-600 dark:text-e2o-400 flex items-center gap-1">
                            <CheckCircle size={11} /> Formulaire pré-rempli pour <strong>{selectedContact.prenom} {selectedContact.nom}</strong>
                            <button onClick={() => { setSelectedContact(null); setQuery(''); setMail(''); setContactPrestaId(null); setContactConferencierId(null); }} className="ml-1 text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300">
                                <X size={11} />
                            </button>
                        </p>
                    )}
                </div>

                {/* Sélecteur de rôle */}
                <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-2">Rôle du destinataire</label>
                    <div className="flex flex-wrap gap-2">
                        {roles.length === 0
                            ? <SpinnerIcon size={13} className="animate-spin text-e2o-400" />
                            : roles.map(role => (
                                <button
                                    key={role.id_event_contact_type}
                                    type="button"
                                    onClick={() => setSelectedRole(parseInt(role.id_event_contact_type, 10))}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedRole === parseInt(role.id_event_contact_type, 10)
                                        ? 'bg-e2o-500 border-e2o-500 text-white'
                                        : 'border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-neutral-300 hover:border-e2o-400 hover:text-e2o-600 dark:hover:text-e2o-400'
                                        }`}
                                >
                                    {role.libelle}
                                </button>
                            ))
                        }
                    </div>
                </div>

                <div className="flex gap-6">
                    {/* QR Code */}
                    <div className="flex flex-col items-center gap-2 shrink-0">
                        <img
                            src={qrSrc}
                            alt="QR code inscription"
                            className="w-32 h-32 rounded-lg border border-gray-100 dark:border-neutral-800"
                        />
                        <button
                            onClick={handleDownloadQr}
                            className="text-xs text-e2o-500 hover:underline flex items-center gap-1"
                        >
                            <UploadCloud size={11} className="rotate-180" />
                            Télécharger
                        </button>
                    </div>

                    <div className="flex-1 space-y-4">
                        {/* Lien à copier */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">
                                Lien d'inscription{selectedContact ? ' (pré-rempli)' : ''}
                            </label>
                            <div className="flex gap-2">
                                <input
                                    readOnly
                                    value={selfRegisterUrl}
                                    className="flex-1 border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-2 text-xs font-mono bg-gray-50 dark:bg-neutral-800 text-gray-600 dark:text-neutral-300 focus:outline-none"
                                />
                                <button
                                    onClick={handleCopy}
                                    className="shrink-0 flex items-center gap-1 px-3 py-2 text-xs font-medium border border-gray-200 dark:border-neutral-700 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-800 dark:text-neutral-200"
                                >
                                    {copied ? <CheckCircle size={13} className="text-green-500" /> : <Copy size={13} />}
                                    {copied ? 'Copié !' : 'Copier'}
                                </button>
                                <a
                                    href={selfRegisterUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="shrink-0 flex items-center gap-1 px-3 py-2 text-xs font-medium border border-gray-200 dark:border-neutral-700 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-800 dark:text-neutral-200"
                                >
                                    <ExternalLink size={13} />
                                    Voir
                                </a>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100 dark:border-neutral-800" /></div>
                            <div className="relative flex justify-center"><span className="bg-white dark:bg-neutral-900 px-2 text-xs text-gray-400 dark:text-neutral-500">ou envoyer par email</span></div>
                        </div>

                        {/* Envoi par email */}
                        <div className="space-y-2">
                            <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400">Email du destinataire</label>
                            <input
                                type="email"
                                value={mail}
                                onChange={e => {
                                    setMail(e.target.value);
                                    setSent(false); // Reset sent status when email changes
                                }}
                                placeholder="contact@exemple.com"
                                className="w-full border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-e2o-400"
                            />
                            {sent && <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle size={12} /> Email envoyé.</p>}
                            <button
                                onClick={handleSend}
                                disabled={!mail || sending}
                                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white bg-e2o-500 hover:bg-e2o-600 rounded-lg disabled:opacity-50 transition-colors"
                            >
                                <Mail size={14} />
                                {sending ? 'Envoi…' : 'Envoyer le lien'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Historique des envois */}
                {sendHistory.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-gray-100 dark:border-neutral-800">
                        <p className="text-xs font-semibold text-gray-400 dark:text-neutral-500 uppercase tracking-wide mb-2">
                            Historique des envois
                        </p>
                        <ul className="space-y-1.5 max-h-36 overflow-y-auto">
                            {sendHistory.map(log => (
                                <li key={log.id_activity_log} className="flex items-center justify-between gap-3 text-xs text-gray-600 dark:text-neutral-400">
                                    <span className="flex items-center gap-1.5 min-w-0">
                                        <Mail size={11} className="text-e2o-400 shrink-0" />
                                        <span className="truncate">{log.metadata?.receiver || log.metadata?.email || '—'}</span>
                                    </span>
                                    <span className="text-gray-400 dark:text-neutral-500 shrink-0">
                                        {new Date(log.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

const RegistrationDashbordCaptationList = ({ isAdmin, profile }) => {
    const { apiUrlTranslation } = useApiUrlTranslation();
    const { currentEvent } = useContext(EventContext);
    const [videoList, setVideoList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showAddSlot, setShowAddSlot] = useState(false);
    const [showSendLink, setShowSendLink] = useState(false);
    const [youtubeOrgs, setYoutubeOrgs] = useState([]);

    // Un seul fetch pour toutes les VideoCards — évite N requêtes simultanées
    useEffect(() => {
        const fd = new FormData();
        fd.append('action', 'get_youtube_orgs');
        fetch(UPLOAD_API_URL, { method: 'POST', headers: { 'X-Api-Key': UPLOAD_API_KEY }, body: fd })
            .then(r => r.json())
            .then(d => { if (d.orgs?.length) setYoutubeOrgs(d.orgs); })
            .catch(() => { });
    }, []);

    // Met à jour localement le youtube_id d'un item sans recharger toute la liste
    const updateVideoInList = (id_conf_event, id_video, id_youtube, ai = null, chapters = null) => {
        setVideoList(prev => prev.map(item => {
            if (String(item.conf_event?.id_conf_event) !== String(id_conf_event)) return item;
            return {
                ...item,
                ...(ai ? { aiSuggestions: ai } : {}),
                ...(chapters ? { chapters } : {}),
                video: {
                    ...item.video,
                    id_video: {
                        ...(item.video?.id_video || {}),
                        id_video,
                        id_youtube,
                    },
                    video_url: `https://www.youtube.com/watch?v=${id_youtube}`,
                },
            };
        }));
    };

    useEffect(() => {
        if (!currentEvent?.id_event) return;

        const fetchVideoData = async () => {
            try {
                setLoading(true);

                // 1. Récupérer tous les participants avec un créneau réservé (statut 143)
                const participantsRes = await fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_event=${currentEvent.id_event} AND cf.statut=143 AND cf.id_conf_event NOT IN (177085, 177140)`);
                if (!participantsRes.ok) throw new Error('Erreur réseau lors de la récupération des participants.');
                const participants = await participantsRes.json();

                if (participants.length === 0) {
                    setVideoList([]);
                    setLoading(false);
                    return;
                }

                // 2. Extraire les IDs des contacts et des créneaux
                const contactIds = participants.map(p => p.contact.id_contact).filter(Boolean);
                const confEventIds = participants.map(p => p.conf_event?.id_conf_event).filter(Boolean);

                // 3. Récupérer les prestations et les vidéos en parallèle
                let prestas = [];
                let videos = [];

                const promisesToResolve = [];

                if (contactIds.length > 0) {
                    const prestasUrl = `${apiUrlTranslation}?action=getPrestaList&params= WHERE id_contact IN (${contactIds.join(',')})`;
                    promisesToResolve.push(fetch(prestasUrl).then(res => res.json()));
                } else {
                    promisesToResolve.push(Promise.resolve([])); // Placeholder pour Promise.all
                }

                if (confEventIds.length > 0) {
                    const filter = `WHERE vc.id_conf_event IN (${confEventIds.join(',')})`;
                    const videosUrl = `${apiUrlTranslation}?action=getVideosConferences&filter=${encodeURIComponent(filter)}`;
                    promisesToResolve.push(fetch(videosUrl).then(res => res.json()));
                } else {
                    promisesToResolve.push(Promise.resolve([])); // Placeholder pour Promise.all
                }

                const [prestasResult, videosResult] = await Promise.all(promisesToResolve);
                prestas = prestasResult || [];
                videos = videosResult || [];


                // 4. Créer une map des prestations par id_contact
                const prestasMap = new Map();
                prestas.forEach(presta => {
                    if (!prestasMap.has(presta.id_contact)) {
                        prestasMap.set(presta.id_contact, []);
                    }
                    prestasMap.get(presta.id_contact).push(presta);
                });

                // 5. Créer une map des vidéos par id_conf_event pour une recherche efficace
                const videosMap = new Map(videos.map(video => [String(video.id_conf_event.id_conf_event), video]));

                // 6. Regrouper par créneau : un slot = une entrée, N contacts possibles
                const slotsMap = new Map();
                participants.forEach(participant => {
                    const confEventId = participant.conf_event?.id_conf_event;
                    if (!confEventId) return;

                    if (!slotsMap.has(confEventId)) {
                        slotsMap.set(confEventId, {
                            conf_event: participant.conf_event,
                            contacts: [],
                            conferenciers: [],
                            presta: undefined,
                            video: videosMap.get(String(confEventId)),
                        });
                    }

                    const slot = slotsMap.get(confEventId);
                    slot.contacts.push(participant.contact);
                    slot.conferenciers.push({ id_conferencier: participant.id_conferencier });

                    if (!slot.presta) {
                        const participantPrestas = prestasMap.get(participant.contact.id_contact);
                        if (participantPrestas?.[0]) slot.presta = participantPrestas[0];
                    }
                });

                const combinedData = Array.from(slotsMap.values());

                setVideoList(combinedData);

                // 7. Pour chaque vidéo YouTube existante, vérifier et compléter les liaisons
                const itemsWithVideo = combinedData.filter(item => item.video?.id_video?.id_video && item.video?.id_video?.id_youtube);
                if (itemsWithVideo.length > 0) {
                    const idVideoList = itemsWithVideo.map(item => item.video.id_video.id_video);

                    const [videosEvents, videosContacts] = await Promise.all([
                        fetch(`${apiUrlTranslation}?action=getVideosEvents&id_event=${currentEvent.id_event}`).then(r => r.json()).catch(() => []),
                        fetch(`${apiUrlTranslation}?action=getVideosContacts&filter=${encodeURIComponent(`WHERE id_video IN (${idVideoList.join(',')})`)}`).then(r => r.json()).catch(() => []),
                    ]);

                    const linkedEventVideoIds = new Set((videosEvents || []).map(ve => String(ve.id_video?.id_video ?? ve.id_video)));
                    const linkedContactVideoIds = new Set((videosContacts || []).map(vc => String(vc.id_video?.id_video ?? vc.id_video)));

                    const linkPromises = [];
                    for (const item of itemsWithVideo) {
                        const id_video = item.video.id_video.id_video;
                        const id_event = currentEvent.id_event;

                        if (!linkedEventVideoIds.has(String(id_video))) {
                            linkPromises.push(
                                fetch(`${API_URL}?action=createVideosEvents`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ id_video, id_event }),
                                }).catch(() => { })
                            );
                        }
                        for (const contact of (item.contacts || [])) {
                            const id_contact = contact?.id_contact;
                            if (id_contact && !linkedContactVideoIds.has(String(id_video))) {
                                linkPromises.push(
                                    fetch(`${API_URL}?action=createVideosContact`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ id_video, id_contact }),
                                    }).catch(() => { })
                                );
                            }
                        }
                    }
                    if (linkPromises.length > 0) await Promise.all(linkPromises);
                }

            } catch (err) {
                setError(err.message);
                console.error("Erreur lors de la récupération des données vidéo:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchVideoData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentEvent?.id_event]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return <Layout><div className="text-center p-10 m-10"><Loader /></div></Layout>;
    if (error) return <Layout><div className="text-center p-10 text-red-500">Erreur: {error}</div></Layout>;

    return (
        <RegistrationDashboardLayout

            isAdmin={isAdmin}
            profile={profile}
        >
            <div className="p-fib-8 bg-gray-50 dark:bg-neutral-950 min-h-screen">
                <header className="mb-fib-8 flex flex-col sm:flex-row sm:items-center justify-between gap-fib-3">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 dark:text-white pt-fib-5 leading-phi">Vidéos des Présentations</h1>
                        <p className="text-gray-600 dark:text-neutral-400 mt-1 leading-phi">Retrouvez l'ensemble des présentations enregistrées sur le plateau.</p>
                    </div>
                    {isAdmin && (
                        <div className="flex items-center gap-fib-2 mt-fib-5">
                            <button
                                onClick={() => setShowSendLink(true)}
                                className="flex items-center gap-fib-2 px-fib-3 py-fib-2 text-sm font-medium text-gray-700 dark:text-neutral-200 bg-white dark:bg-neutral-900 border border-gray-300 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800 rounded-fib-5"
                            >
                                <Mail size={15} />
                                Lien d'inscription
                            </button>
                            <button
                                onClick={() => setShowAddSlot(true)}
                                className="flex items-center gap-fib-2 px-fib-3 py-fib-2 text-sm font-medium text-white bg-e2o-500 hover:bg-e2o-600 rounded-fib-5"
                            >
                                <Plus size={15} />
                                Ajouter un slot
                            </button>
                        </div>
                    )}

                    {showSendLink && (
                        <SendSlotLinkModal
                            currentEvent={currentEvent}
                            onClose={() => setShowSendLink(false)}
                            profile={profile}
                        />
                    )}
                </header>

                {showAddSlot && (
                    <AddSlotModal
                        onClose={() => setShowAddSlot(false)}
                        onCreated={({ contact, id_conf_event, id_conferencier }) => {
                            setVideoList(prev => [{
                                conf_event: { id_conf_event },
                                contacts: [contact],
                                conferenciers: [{ id_conferencier }],
                                presta: undefined,
                                video: undefined,
                            }, ...prev]);
                            setShowAddSlot(false);
                        }}
                        currentEvent={currentEvent}
                        apiUrlTranslation={apiUrlTranslation}
                    />
                )}

                {videoList.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {videoList.map(data => (
                            <VideoCard
                                key={data.conf_event?.id_conf_event ?? 'no-ce'}
                                data={data}
                                isAdmin={isAdmin}
                                onVideoUploaded={updateVideoInList}
                                onDeleted={id => setVideoList(prev => prev.filter(item => String(item.conf_event?.id_conf_event) !== String(id)))}
                                youtubeOrgs={youtubeOrgs}
                                profile={profile}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center bg-white dark:bg-neutral-900 p-10 rounded-lg shadow-sm">
                        <Video size={48} className="mx-auto text-gray-300 dark:text-neutral-700" />
                        <h2 className="mt-4 text-xl font-semibold text-gray-700 dark:text-neutral-200">Aucune vidéo disponible</h2>
                        <p className="mt-1 text-gray-500 dark:text-neutral-400">Les présentations enregistrées apparaîtront ici.</p>
                    </div>
                )}
            </div>
        </RegistrationDashboardLayout>
    );
};

export { SendSlotLinkModal };
export default RegistrationDashbordCaptationList;